import { ValueOf } from 'type-fest';

export const TAX_ASSIST_URL_PRODUCTION = 'https://assist.harness.co';
export const TAX_ASSIST_URL_DEV = 'https://assist.harness-dev.com';

export const DEFAULT_DEBOUNCED_SEARCH_TIME = 500;

// Marks the request as XHR so StartSession does not record it as the
// session's previous URL, which Fortify's confirm response redirects back to.
export const xhrHeaders = {
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
};

export enum Permission {
  MANAGE_USERS = 'manage_users',
  MANAGE_TEAMS = 'manage_teams',
  MANAGE_ROLES = 'manage_roles',
  MANAGE_TAXPAYERS = 'manage_taxpayers',
  MANAGE_CONTACTS = 'manage_contacts',
  MANAGE_INVOICES = 'manage_invoices',
  TEAM_MANAGE_INVOICES = 'team_manage_invoices',
  TEAM_MANAGE_TAXPAYERS = 'team_manage_taxpayers',
  DELETE_FILES = 'delete_files',
  DELETE_ENGAGEMENTS = 'delete_engagements',
  MANAGE_RENEWALS = 'manage_renewals',
  TEAM_MANAGE_RENEWALS = 'team_manage_renewals',
  TEAM_MANAGE_TEAMS = 'team_manage_teams',
  TEAM_DELETE_FILES = 'team_delete_files',
  TEAM_DELETE_ENGAGEMENTS = 'team_delete_engagements',
  TEAM_ALLOW_MULTIPLE_FIRMS = 'team_allow_multiple_firms',
}

export enum TaxpayerType {
  INDIVIDUAL = 'individual',
  BUSINESS = 'entity',
  PARTNERSHIP = 'partnership',
  S_CORP = 's-corp',
  C_CORP = 'c-corp',
  FOREIGN_CORPORATION = 'foreign-corporation',
  TRUST = 'trust',
  NON_PROFIT = 'non-profit',
  EMPLOYEE_BENEFIT_PLAN = 'employee-benefit-plan',
  SMLLC = 'single-member-llc',
}

export enum FilingStatus {
  HEAD_OF_HOUSEHOLD = 'head-of-household',
  MARRIED_FILING_JOINTLY = 'married-filing-jointly',
  MARRIED_FILING_SEPARATELY = 'married-filing-separately',
  SINGLE = 'single',
  QUALIFYING_WIDOWER = 'qualifying-widower',
}

export enum LifecycleStage {
  PROSPECT = 'prospect',
  CURRENT = 'current',
}

export const LifecycleStageLabels: { [key in LifecycleStage]: string } = {
  [LifecycleStage.PROSPECT]: 'Prospect',
  [LifecycleStage.CURRENT]: 'Current',
};

export enum WorkflowScheduleFrequency {
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUALLY = 'annually',
}

export const WorkflowScheduleFrequencyLabels: {
  [key in WorkflowScheduleFrequency]: string;
} = {
  [WorkflowScheduleFrequency.WEEKLY]: 'Weekly',
  [WorkflowScheduleFrequency.BIWEEKLY]: 'Bi-weekly',
  [WorkflowScheduleFrequency.MONTHLY]: 'Monthly',
  [WorkflowScheduleFrequency.QUARTERLY]: 'Quarterly',
  [WorkflowScheduleFrequency.ANNUALLY]: 'Annually',
};

export enum DueDateRuleType {
  NONE = 'none',
  SPECIFIC_DATE = 'specific_date',
  DAYS_AFTER_START = 'days_after_start',
  DAY_OF_MONTH = 'day_of_month',
  LAST_DAY_OF_MONTH = 'last_day_of_month',
  DAY_OF_FOLLOWING_MONTH = 'day_of_following_month',
}

export const DueDateRuleTypeLabels: {
  [key in DueDateRuleType]: string;
} = {
  [DueDateRuleType.NONE]: 'No due date',
  [DueDateRuleType.SPECIFIC_DATE]: 'A specific date',
  [DueDateRuleType.DAYS_AFTER_START]: 'A number of days after the start date',
  [DueDateRuleType.DAY_OF_MONTH]: 'A day of the month',
  [DueDateRuleType.LAST_DAY_OF_MONTH]: 'The last day of the month',
  [DueDateRuleType.DAY_OF_FOLLOWING_MONTH]: 'A day of the following month',
};

export enum StartDateRuleType {
  AT_CREATION = 'at_creation',
  SPECIFIC_DATE = 'specific_date',
  DAY_OF_MONTH = 'day_of_month',
  DAYS_BEFORE_DUE = 'days_before_due',
}

export const StartDateRuleTypeLabels: {
  [key in StartDateRuleType]: string;
} = {
  [StartDateRuleType.AT_CREATION]: 'At creation',
  [StartDateRuleType.SPECIFIC_DATE]: 'A specific date',
  [StartDateRuleType.DAY_OF_MONTH]: 'A day of the month',
  [StartDateRuleType.DAYS_BEFORE_DUE]: 'A number of days before the due date',
};

/** The workflow date a template task's due-date offset counts from. */
export enum TaskDateAnchor {
  START = 'start',
  DUE = 'due',
}

export const TaskDateAnchorLabels: {
  [key in TaskDateAnchor]: string;
} = {
  [TaskDateAnchor.START]: 'after the workflow start date',
  [TaskDateAnchor.DUE]: 'before the workflow due date',
};

// Canonical name of the firm's default "advisory" tag (see Tag::ADVISORY).
// Tag names are stored lowercased and trimmed, so compare against the
// normalized form.
export const ADVISORY_TAG_NAME = 'advisory';

export const FILING_STATUS_NOT_SET = 'not-set';
export const FILING_STATUS_NOT_SET_LABEL = 'Filing status not set';

export const FilingStatusLabels: { [key in FilingStatus]: string } = {
  [FilingStatus.HEAD_OF_HOUSEHOLD]: 'Head of Household',
  [FilingStatus.MARRIED_FILING_JOINTLY]: 'Married Filing Jointly',
  [FilingStatus.MARRIED_FILING_SEPARATELY]: 'Married Filing Separately',
  [FilingStatus.SINGLE]: 'Single',
  [FilingStatus.QUALIFYING_WIDOWER]: 'Qualifying Widow(er)',
};

export const TaxpayerTypeLabels: { [key in TaxpayerType]: string } = {
  [TaxpayerType.INDIVIDUAL]: 'Individual',
  [TaxpayerType.BUSINESS]: 'Entity',
  [TaxpayerType.PARTNERSHIP]: 'Partnership',
  [TaxpayerType.S_CORP]: 'S-Corp',
  [TaxpayerType.C_CORP]: 'C-Corp',
  [TaxpayerType.FOREIGN_CORPORATION]: 'Foreign Corporation',
  [TaxpayerType.TRUST]: 'Trust/estate',
  [TaxpayerType.NON_PROFIT]: 'Non-Profit',
  [TaxpayerType.EMPLOYEE_BENEFIT_PLAN]: 'Employee Benefit Plan',
  [TaxpayerType.SMLLC]: 'Single-Member LLC',
};

export enum TaxpayerSelectDataType {
  ClientType = 'ClientType',
  AdvisorTeam = 'AdvisorTeam',
  OtherTeam = 'OtherTeam',
}

/**
 * Change the type of Keys of T from NewType
 */
export type ChangeTypeOfKeys<
  T extends object,
  Keys extends keyof T,
  NewType,
> = {
  // Loop to every key. We will check if the key
  // is assignable to Keys. If yes, change the type.
  // Else, retain the type.
  [key in keyof T]: key extends Keys ? NewType : T[key];
};

export type SetDataByObject<TForm> = (data: TForm) => void;

export type SetDataByMethod<TForm> = (
  data: (previousData: TForm) => TForm,
) => void;

export type SetDataByKeyValuePair<TForm> = <K extends keyof TForm>(
  key: K,
  value: ValueOf<TForm>,
) => void;

export const SUPPORT_EMAIL = 'support@harnesswealth.com';

export enum AlertActionVariant {
  setAdvisor = 'setAdvisor',
}

export const DEFAULT_PAGE_SIZE = 200;

// matches "2024 Q2"
export const yearQuarterRegex = /(\d{4})\sQ(\d)/;

export enum ViewMode {
  Create = 'create',
  Edit = 'edit',
}

// Note, this list needs to be kept in sync with file tag definitions in Socrates
export enum FileTags {
  WAGES_AND_SALARIES = 'wages_and_salaries',
  BROKERAGE = 'brokerage',
  RETIREMENT = 'retirement',
  EQUITY = 'equity',
  HOME_AND_RENTAL_PROPERTIES = 'home_and_rental_properties',
  PROPERTY = 'property',
  SELF_EMPLOYMENT = 'self_employment',
  EXPENSES_AND_DEDUCTIONS = 'expenses_and_deductions',
  PARTNERSHIP_SCORP_TRUST_INCOME = 'partnership_scorp_trust_income',
  HEALTHCARE_AND_INSURANCE = 'healthcare_and_insurance',
  CRYPTO = 'crypto',
  OTHER = 'other',
  ESTIMATED_PAYMENT_RECEIPT = 'est_payment_receipt',
  FINAL_RETURN_AUTHORIZATION = 'final_return_authorization',
  FINAL_RETURN_ACCEPTANCE = 'final_return_acceptance',
  EXTENSION_AUTHORIZATION = 'extension_authorization',
  EXTENSION_ACCEPTANCE = 'extension_acceptance',
  QUESTIONNAIRE = 'questionnaire',
  BANK_ACCOUNT_INFO = 'bank_account_info',
  ENGAGEMENT_LETTER = 'engagement_letter',
  INVOICE = 'invoice',
  FINANCIAL_STATEMENTS = 'financial_statements',
  ENTITY_GOVERNANCE = 'entity_governance',
  PAYROLL_AND_BENEFITS = 'payroll_and_benefits',
  ESTIMATED_PAYMENTS = 'estimated_payments',
  IDENTIFICATION = 'identification',
  ESTATE_PLANNING = 'estate_planning',
  TAX_RETURN = 'tax_return',
  CORRESPONDANCE = 'correspondance',
  INCOME = 'income',
  ASSETS_AND_LIABILITIES = 'assets_and_liabilities',
  FINAL_RETURN = 'final_return',
  K_1 = 'k_1',
  _1099 = '1099',
  PROJECTIONS = 'projections',
  ESTIMATED_TAX = 'estimated_tax',
  FBAR = 'fbar',
  ACCOUNTING_REVIEW = 'accounting_review',
  DELIVERABLE = 'deliverable',
}

export const fileTags: Partial<Record<FileTags, string>> = {
  [FileTags.ASSETS_AND_LIABILITIES]: 'Assets and liabilities',
  [FileTags.BANK_ACCOUNT_INFO]: 'Bank account info',
  [FileTags.BROKERAGE]: 'Brokerage',
  [FileTags.CORRESPONDANCE]: 'Federal and state correspondence',
  [FileTags.CRYPTO]: 'Crypto',
  [FileTags.ENGAGEMENT_LETTER]: 'Engagement letter',
  [FileTags.EQUITY]: 'Equity',
  [FileTags.ESTIMATED_PAYMENT_RECEIPT]: 'Est. Payment Receipt',
  [FileTags.ESTIMATED_PAYMENTS]: 'Estimated payments',
  [FileTags.ESTATE_PLANNING]: 'Estate planning',
  [FileTags.EXPENSES_AND_DEDUCTIONS]: 'Expenses and deductions',
  [FileTags.EXTENSION_ACCEPTANCE]: 'Extension acceptance',
  [FileTags.EXTENSION_AUTHORIZATION]: 'Extension authorization',
  [FileTags.FINAL_RETURN_ACCEPTANCE]: 'Final return acceptance',
  [FileTags.FINAL_RETURN_AUTHORIZATION]: 'Final return authorization',
  [FileTags.FINANCIAL_STATEMENTS]: 'Financial statements',
  [FileTags.HEALTHCARE_AND_INSURANCE]: 'Healthcare and insurance',
  [FileTags.IDENTIFICATION]: 'Identification',
  [FileTags.INCOME]: 'Income',
  [FileTags.INVOICE]: 'Invoice',
  [FileTags.ENTITY_GOVERNANCE]: 'Entity governance',
  [FileTags.PARTNERSHIP_SCORP_TRUST_INCOME]:
    'Partnership, S-corp, Trust income',
  [FileTags.PAYROLL_AND_BENEFITS]: 'Payroll and benefits',
  [FileTags.PROPERTY]: 'Property',
  [FileTags.QUESTIONNAIRE]: 'Tax questionnaire',
  [FileTags.RETIREMENT]: 'Retirement',
  [FileTags.SELF_EMPLOYMENT]: 'Self-employment',
  [FileTags.TAX_RETURN]: 'Historical tax returns',
  [FileTags.WAGES_AND_SALARIES]: 'Wages and salaries',
  [FileTags.ACCOUNTING_REVIEW]: 'Accounting review',
  [FileTags.DELIVERABLE]: 'Deliverable',
  [FileTags.OTHER]: 'Other',
};

export const individualFileTags = [
  FileTags.BROKERAGE,
  FileTags.WAGES_AND_SALARIES,
  FileTags.CRYPTO,
  FileTags.HEALTHCARE_AND_INSURANCE,
  FileTags.PROPERTY,
  FileTags.PARTNERSHIP_SCORP_TRUST_INCOME,
  FileTags.RETIREMENT,
  FileTags.SELF_EMPLOYMENT,
];

export const entityFileTags = [
  FileTags.INCOME,
  FileTags.ASSETS_AND_LIABILITIES,
  FileTags.FINANCIAL_STATEMENTS,
  FileTags.ENTITY_GOVERNANCE,
  FileTags.PAYROLL_AND_BENEFITS,
  FileTags.ESTIMATED_PAYMENTS,
];

export const individualAndEntityFileTags = [
  FileTags.EQUITY,
  FileTags.EXPENSES_AND_DEDUCTIONS,
  FileTags.ESTIMATED_PAYMENT_RECEIPT,
  FileTags.FINAL_RETURN_AUTHORIZATION,
  FileTags.FINAL_RETURN_ACCEPTANCE,
  FileTags.OTHER,
];

const taxpayerPermRecordIndividualTags = [
  FileTags.IDENTIFICATION,
  FileTags.ESTATE_PLANNING,
  FileTags.PROPERTY,
  FileTags.EQUITY,
  FileTags.TAX_RETURN,
  FileTags.OTHER,
];

const taxpayerPermRecordEntityTags = [
  FileTags.ENTITY_GOVERNANCE,
  FileTags.PROPERTY,
  FileTags.EQUITY,
  FileTags.CORRESPONDANCE,
  FileTags.TAX_RETURN,
  FileTags.OTHER,
];

export const fileTagLabels: Partial<Record<FileTags, string>> = {
  [FileTags.ASSETS_AND_LIABILITIES]: 'Assets & Liabilities',
  [FileTags.BANK_ACCOUNT_INFO]: 'Bank account info',
  [FileTags.BROKERAGE]: 'Brokerage',
  [FileTags.CORRESPONDANCE]: 'Correspondence',
  [FileTags.CRYPTO]: 'Crypto',
  [FileTags.ENGAGEMENT_LETTER]: 'Engagement letter',
  [FileTags.ENTITY_GOVERNANCE]: 'Entity governance',
  [FileTags.EQUITY]: 'Equity',
  [FileTags.ESTIMATED_PAYMENT_RECEIPT]: 'Est. Payment Receipt',
  [FileTags.ESTIMATED_PAYMENTS]: 'Est. Payments',
  [FileTags.ESTATE_PLANNING]: 'Estate Plan',
  [FileTags.EXPENSES_AND_DEDUCTIONS]: 'Expenses',
  [FileTags.EXTENSION_ACCEPTANCE]: 'Extension acceptance',
  [FileTags.EXTENSION_AUTHORIZATION]: 'Extension authorization',
  [FileTags.FINAL_RETURN_ACCEPTANCE]: 'Final return acceptance',
  [FileTags.FINAL_RETURN_AUTHORIZATION]: 'Final return authorization',
  [FileTags.FINANCIAL_STATEMENTS]: 'Fin. Statement',
  [FileTags.HEALTHCARE_AND_INSURANCE]: 'Health & Insurance',
  [FileTags.HOME_AND_RENTAL_PROPERTIES]: 'Property',
  [FileTags.IDENTIFICATION]: 'ID',
  [FileTags.INCOME]: 'Income',
  [FileTags.INVOICE]: 'Invoice',
  [FileTags.PARTNERSHIP_SCORP_TRUST_INCOME]: 'Entity income',
  [FileTags.PAYROLL_AND_BENEFITS]: 'Payroll',
  [FileTags.PROPERTY]: 'Property',
  [FileTags.QUESTIONNAIRE]: 'Tax questionnaire',
  [FileTags.RETIREMENT]: 'Retirement',
  [FileTags.SELF_EMPLOYMENT]: 'Self-employment',
  [FileTags.TAX_RETURN]: 'Tax return',
  [FileTags.WAGES_AND_SALARIES]: 'Wages',
  [FileTags.OTHER]: 'Other',
  [FileTags.FINAL_RETURN]: 'Final return',
  [FileTags.K_1]: 'K-1',
  [FileTags._1099]: '1099',
  [FileTags.PROJECTIONS]: 'Projections',
  [FileTags.ESTIMATED_TAX]: 'Estimated tax',
  [FileTags.FBAR]: 'FBAR',
  [FileTags.ACCOUNTING_REVIEW]: 'Accounting review',
  [FileTags.DELIVERABLE]: 'Deliverable',
};

export const advisorTags = [
  FileTags.FINAL_RETURN,
  FileTags.K_1,
  FileTags._1099,
  FileTags.PROJECTIONS,
  FileTags.FINANCIAL_STATEMENTS,
  FileTags.ACCOUNTING_REVIEW,
  FileTags.ESTIMATED_TAX,
  FileTags.INVOICE,
  FileTags.FBAR,
  FileTags.DELIVERABLE,
  FileTags.OTHER,
];

export enum UploadFor {
  Deliverable = 'deliverable',
  OnBehalfOfClient = 'client_file',
}

export const tagsAsList = (
  uploadFor?: UploadFor,
  taxpayerType?: string,
  isTaxpayerPermRecord?: boolean,
): { label: string; value: string }[] => {
  const getTagList = (tags: string[]): { label: string; value: string }[] =>
    tags.map((key) => ({
      value: key,
      label: fileTagLabels[key as FileTags] || '',
    }));

  if (uploadFor === UploadFor.Deliverable) {
    return getTagList(advisorTags);
  }

  if (isTaxpayerPermRecord) {
    if (taxpayerType === TaxpayerType.INDIVIDUAL) {
      return getTagList(taxpayerPermRecordIndividualTags);
    }

    if (taxpayerType && taxpayerType !== TaxpayerType.INDIVIDUAL) {
      return getTagList(taxpayerPermRecordEntityTags);
    }
  }

  if (taxpayerType === TaxpayerType.INDIVIDUAL) {
    const allIndividualTags = isTaxpayerPermRecord
      ? [...taxpayerPermRecordIndividualTags]
      : [...individualFileTags, ...individualAndEntityFileTags];
    return getTagList(allIndividualTags);
  }

  if (taxpayerType && taxpayerType !== TaxpayerType.INDIVIDUAL) {
    const tags = isTaxpayerPermRecord
      ? [...taxpayerPermRecordEntityTags]
      : [...entityFileTags, ...individualAndEntityFileTags];
    return getTagList(tags);
  }

  return getTagList(advisorTags);
};

export type Option<T> = {
  value: T;
  label: string;
  helperText?: string;
};

/**
 * What a client document is from the firm's point of view (HWEPD-10256).
 * Mirrors App\Enums\ClientDocumentKind.
 */
export enum ClientDocumentKind {
  deliverable = 'deliverable',
  fromClient = 'client',
  internalOnly = 'internal',
}

/**
 * Row-chip labels. The chip sits next to a lock icon in a narrow column, so
 * internal reads as the short "Internal"; the Filters menu keeps the fuller
 * "Internal only" it gets from the server.
 */
export const clientDocumentKindLabels: Record<ClientDocumentKind, string> = {
  [ClientDocumentKind.deliverable]: 'Deliverable',
  [ClientDocumentKind.fromClient]: 'From client',
  [ClientDocumentKind.internalOnly]: 'Internal',
};

/** Tooltips on a disabled Delete, so the action is explained rather than hidden. */
export const FILE_DELETE_BLOCKED_REASONS = {
  attached: 'Attached to a task or analysis. Remove it there instead.',
  noPermission: "You don't have permission to delete this file.",
  uploaderOnly: 'Only the person who uploaded this file can delete it.',
} as const;

/** Mirrors App\Enums\DocumentCategory; uncategorised is null, not an option. */
export enum DocumentCategory {
  taxReturns = 'tax_returns',
  taxCorrespondence = 'tax_correspondence',
  bankingStatements = 'banking_statements',
  financialStatements = 'financial_statements',
  payrollRecords = 'payroll_records',
  legalEntity = 'legal_entity',
  sourceDocuments = 'source_documents',
  fixedAssets = 'fixed_assets',
  advisoryPlanning = 'advisory_planning',
  engagementAdmin = 'engagement_admin',
}

export const documentCategoryLabels: Record<DocumentCategory, string> = {
  [DocumentCategory.taxReturns]: 'Tax Returns',
  [DocumentCategory.taxCorrespondence]: 'Tax Correspondence & Notices',
  [DocumentCategory.bankingStatements]: 'Banking & Credit Card Statements',
  [DocumentCategory.financialStatements]: 'Financial Statements & Reports',
  [DocumentCategory.payrollRecords]: 'Payroll & Employment Records',
  [DocumentCategory.legalEntity]: 'Legal & Entity Documents',
  [DocumentCategory.sourceDocuments]: 'Source Documents',
  [DocumentCategory.fixedAssets]: 'Fixed Assets',
  [DocumentCategory.advisoryPlanning]: 'Advisory & Planning',
  [DocumentCategory.engagementAdmin]: 'Engagement & Administration',
};

/** The categories in the order every picker and filter lists them: by label. */
export const documentCategoriesByLabel: DocumentCategory[] = Object.values(
  DocumentCategory,
).sort((left, right) =>
  documentCategoryLabels[left].localeCompare(documentCategoryLabels[right]),
);

/** The categories as chip menu items, in picker order. */
export const documentCategoryOptions: {
  value: DocumentCategory;
  label: string;
}[] = documentCategoriesByLabel.map((value) => ({
  value,
  label: documentCategoryLabels[value],
}));

/** Whether a value read from the URL names a category. */
export const isDocumentCategory = (value: string): value is DocumentCategory =>
  (Object.values(DocumentCategory) as string[]).includes(value);

/** The one Socrates tag offered beside the categories, because a final return is marked by it. */
export const finalReturnOption = {
  value: FileTags.FINAL_RETURN,
  label: fileTagLabels[FileTags.FINAL_RETURN] ?? '',
};

export enum FilesFilter {
  engagementFiles = 'engagementFiles',
  allEngagementFiles = 'allEngagementFiles',
  taxpayerPermRecords = 'taxpayerPermRecords',
  contactLevelFiles = 'contactLevelFiles',
  workflowFiles = 'workflowFiles',
  allWorkflowFiles = 'allWorkflowFiles',
  internalFiles = 'internalFiles',
  allFiles = 'allFiles',
}
